import React, { useState } from 'react';
import {
  Grid,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel
} from '@mui/material';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { memberfulQueries } from '../../../graphql/queries';
import mutations from '../../../graphql/mutations.js';
import AdminPage from '../../../Components/AdminPage.js';
import MemberfulPreviewModal from './MemberfulPreviewModal.js';
import MemberfulHistoryDetail from './MemberfulHistoryDetail.js';
import MemberfulPlanMapping from './MemberfulPlanMapping.js';
import { formatBatchMessage, filterMembersByPaymentStatus } from '../../../helpers/memberful.js';

const MembersTab = () => {
  const [planFilter, setPlanFilter] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('all');
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [previewEmail, setPreviewEmail] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [batchMessage, setBatchMessage] = useState(null);
  const [membersData, setMembersData] = useState(null);
  const [membersLoading, setMembersLoading] = useState(false);

  const client = useApolloClient();

  const { data: mappingsData, loading: mappingsLoading } = useQuery(memberfulQueries.MemberfulPlanMappings, {
    fetchPolicy: 'no-cache'
  });

  const [migrateBatch, { loading: batchLoading }] = useMutation(mutations.MigrateMemberfulMembers);

  const handleFetchMembers = async () => {
    setMembersLoading(true);
    setBatchMessage(null);
    try {
      const { data } = await client.query({
        query: memberfulQueries.MemberfulMembers(planFilter || null),
        fetchPolicy: 'no-cache'
      });
      setMembersData(data);
    } catch (err) {
      setBatchMessage('Error fetching members: ' + err.message);
    }
    setMembersLoading(false);
  };

  const handleRowClick = (email) => {
    setPreviewEmail(email);
    setPreviewOpen(true);
  };

  const handleCheckboxChange = (email) => {
    setSelectedEmails(prev =>
      prev.indexOf(email) > -1
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked && filteredMembers) {
      setSelectedEmails(filteredMembers.map(m => m.email));
    } else {
      setSelectedEmails([]);
    }
  };

  const handleBatchMigrate = async () => {
    if (selectedEmails.length === 0) return;
    setBatchMessage(null);
    try {
      const { data } = await migrateBatch({ variables: { emails: selectedEmails } });
      const message = formatBatchMessage(selectedEmails, data.migrateMemberfulMembers);
      setSelectedEmails([]);
      // refresh so migrated members drop out of the list and can't be re-selected;
      // the refetch clears batchMessage, so set it afterwards
      await handleFetchMembers();
      setBatchMessage(message);
    } catch (err) {
      setBatchMessage('Batch migration error: ' + err.message);
    }
  };

  const members = membersData ? membersData.memberfulMembers : null;
  const filteredMembers = members
    ? filterMembersByPaymentStatus(members, paymentStatus, planFilter).filter(m =>
      !emailSearch || m.email.toLowerCase().indexOf(emailSearch.toLowerCase()) > -1 ||
        (m.fullName && m.fullName.toLowerCase().indexOf(emailSearch.toLowerCase()) > -1)
    )
    : null;

  return (
    <div>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={3}>
          <FormControl fullWidth size='small'>
            <InputLabel>Filter by subscription</InputLabel>
            <Select
              value={planFilter}
              label='Filter by subscription'
              onChange={(e) => setPlanFilter(e.target.value)}
              disabled={mappingsLoading}
              endAdornment={mappingsLoading ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
            >
              <MenuItem value=''>All</MenuItem>
              {mappingsData && mappingsData.memberfulPlanMappings
                ? mappingsData.memberfulPlanMappings.map(mapping => (
                  <MenuItem key={mapping.memberfulPlanId} value={String(mapping.memberfulPlanId)}>
                    {mapping.memberfulPlanName}
                  </MenuItem>
                ))
                : null}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <TextField
            size='small'
            fullWidth
            label='Search by email or name'
            value={emailSearch}
            onChange={(e) => setEmailSearch(e.target.value)}
          />
        </Grid>
        <Grid item xs={2}>
          <Button
            variant='contained'
            onClick={handleFetchMembers}
            disabled={membersLoading}
          >
            {membersLoading ? <CircularProgress size={24} /> : 'Fetch Members'}
          </Button>
        </Grid>
        <Grid item xs={4} sx={{ textAlign: 'right' }}>
          {selectedEmails.length > 0 && (
            <Button
              variant='contained'
              color='warning'
              onClick={handleBatchMigrate}
              disabled={batchLoading}
            >
              {batchLoading
                ? <CircularProgress size={24} />
                : 'Migrate Selected (' + selectedEmails.length + ')'}
            </Button>
          )}
        </Grid>
        <Grid item xs={12}>
          <RadioGroup
            row
            value={paymentStatus}
            onChange={(e) => {
              setPaymentStatus(e.target.value);
              // don't let now-hidden members stay selected for the batch
              setSelectedEmails([]);
            }}
          >
            <FormControlLabel value='all' control={<Radio size='small' />} label='All' />
            <FormControlLabel value='paying' control={<Radio size='small' />} label='Paying' />
            <FormControlLabel value='free' control={<Radio size='small' />} label='Free trial' />
          </RadioGroup>
        </Grid>
      </Grid>

      {batchMessage && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant='body2'
            color={batchMessage.indexOf('error') > -1 ? 'error' : 'success.main'}
          >
            {batchMessage}
          </Typography>
        </Box>
      )}

      {membersLoading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant='body2' sx={{ mt: 1 }}>Loading members from Memberful...</Typography>
        </Box>
      )}

      {filteredMembers && (
        <TableContainer component={Paper}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox'>
                  <Checkbox
                    onChange={handleSelectAll}
                    checked={selectedEmails.length === filteredMembers.length && filteredMembers.length > 0}
                    indeterminate={selectedEmails.length > 0 && selectedEmails.length < filteredMembers.length}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Memberful Plans</TableCell>
                <TableCell>Stripe ID</TableCell>
                <TableCell>Exists in BB</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMembers.map((member) => (
                <TableRow
                  hover
                  key={member.id}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding='checkbox'>
                    <Checkbox
                      checked={selectedEmails.indexOf(member.email) > -1}
                      onChange={() => handleCheckboxChange(member.email)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>
                  <TableCell onClick={() => handleRowClick(member.email)}>
                    {member.fullName}
                  </TableCell>
                  <TableCell onClick={() => handleRowClick(member.email)}>
                    {member.email}
                  </TableCell>
                  <TableCell onClick={() => handleRowClick(member.email)}>
                    {member.subscriptions.filter(s => s.active &&
                      (!planFilter || String(s.plan.id) === planFilter)
                    ).map(s => (
                      <Chip
                        key={s.id}
                        label={s.plan.name}
                        size='small'
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))}
                  </TableCell>
                  <TableCell onClick={() => handleRowClick(member.email)}>
                    {member.stripeCustomerId || '-'}
                  </TableCell>
                  <TableCell onClick={() => handleRowClick(member.email)}>
                    {member.existsInBB
                      ? <Chip label='Yes' color='success' size='small' />
                      : <Chip label='No' color='default' size='small' />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {filteredMembers && filteredMembers.length === 0 && (
        <Typography variant='body2' sx={{ mt: 2 }}>No members found.</Typography>
      )}

      <MemberfulPreviewModal
        open={previewOpen}
        email={previewEmail}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewEmail(null);
        }}
        onMigrated={() => {
          handleFetchMembers();
        }}
      />
    </div>
  );
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(/^\d+$/.test(String(value)) ? parseInt(value) : value);
  if (isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const HistoryTab = () => {
  const columns = [
    { id: 'createdAt', label: 'Date', sortColumn: 'createdAt' },
    { id: 'memberfulEmail', label: 'Email', sortColumn: 'memberfulEmail' },
    { id: 'memberfulFullName', label: 'Name', sortColumn: 'memberfulFullName' },
    { id: 'action', label: 'Action', sortColumn: 'action' },
    { id: 'status', label: 'Status', sortColumn: 'status' }
  ];

  const transformRows = (rows) => {
    if (!rows) return rows;
    return rows.map(row => ({
      ...row,
      createdAt: formatDate(row.createdAt)
    }));
  };

  return (
    <AdminPage
      columns={columns}
      query={memberfulQueries.MemberfulMigrationHistory}
      initialOrderBy='id'
      initialOrderByDirection='desc'
      rowsDataPath='memberfulMigrationHistory.migrations'
      countDataPath='memberfulMigrationHistory.count'
      sectionName='memberfulMigrationHistory'
      transformRows={transformRows}
    >
      {
        (id, onSubmitSuccess, onCancelClick, refetch, openSnackbar) => (
          <>
            {
              id && id > 0
                ? <MemberfulHistoryDetail
                    id={id}
                    onCancelClick={onCancelClick}
                  />
                : null
            }
          </>
        )
      }
    </AdminPage>
  );
};

const MemberfulMigration = () => {
  const [tab, setTab] = useState(0);

  return (
    <div>
      <Typography variant='h5' sx={{ mb: 2 }}>Memberful Migration</Typography>
      <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)} sx={{ mb: 3 }}>
        <Tab label='Members' />
        <Tab label='Plan Mapping' />
        <Tab label='Migration History' />
      </Tabs>
      {tab === 0 && <MembersTab />}
      {tab === 1 && <MemberfulPlanMapping />}
      {tab === 2 && <HistoryTab />}
    </div>
  );
};

export default MemberfulMigration;
